import React, { createContext, useContext, useCallback } from 'react';
import { ClerkProvider, useAuth as useClerkAuth, useUser, useOrganization } from '@clerk/clerk-react';
import type { UserResource, OrganizationResource } from '@clerk/types';

const publishableKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;


if (!publishableKey) {
  throw new Error('Missing Clerk Publishable Key. Please set REACT_APP_CLERK_PUBLISHABLE_KEY in your environment variables.');
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserResource | null | undefined;
  organization: OrganizationResource | null | undefined;
  internalOrgId: string | null;
  isOrgLoading: boolean;
  error: string | null;
  logout: () => void;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const ClerkAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ClerkProvider publishableKey={publishableKey}>
      {children}
    </ClerkProvider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within a ClerkAuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isSignedIn, signOut, getToken, isLoaded: authLoaded } = useClerkAuth();
  const { user } = useUser();
  const { organization } = useOrganization();
  const [internalOrgId, setInternalOrgId] = React.useState<string | null>(null);
  const [isOrgLoading, setIsOrgLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | null>(null);
  const [hasSelectedPersonal, setHasSelectedPersonal] = React.useState<boolean>(false);
  
  // Listen for 401 unauthorized events and try to refresh token before signing out
  React.useEffect(() => {
    const handleUnauthorized = async () => {
      try {
        // Try to get a fresh token (skipCache forces refresh)
        const newToken = await getToken({ skipCache: true });
        if (!newToken) {
          // Token refresh failed, sign out
          signOut();
        }
        // If we got a new token, the retry will happen automatically
      } catch (error) {
        console.error('Token refresh failed:', error);
        signOut();
      }
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [getToken, signOut]);
  
  // Load personal selection for current user on auth change
  React.useEffect(() => {
    if (isSignedIn && user?.id) {
      try {
        const clerkKey = `hasSelectedPersonal_${user.id}`;
        const stored = localStorage.getItem(clerkKey);
        setHasSelectedPersonal(stored === 'true');
      } catch (e) {
        console.warn('Failed to load personal selection from localStorage:', e);
      }
    } else if (!isSignedIn) {
      // Clear state when signed out
      setHasSelectedPersonal(false);
    }
  }, [isSignedIn, user?.id]);
  
  const memoizedGetToken = useCallback(async () => {
    try {
      const token = await getToken();
      return token;
    } catch (error) {
      console.error('Failed to get token:', error);
      return null;
    }
  }, [getToken]);

  // Only detect when user switches FROM an org TO personal (via org switcher in navbar)
  const prevOrgId = React.useRef<string | null>(null);
  React.useEffect(() => {
    // Only set personal if user had an org before and now doesn't (switched via org switcher)
    if (prevOrgId.current && !organization?.id && !hasSelectedPersonal && user?.id) {
      setHasSelectedPersonal(true);
      try {
        const clerkKey = `hasSelectedPersonal_${user.id}`;
        localStorage.setItem(clerkKey, 'true');
      } catch (e) {
        console.warn('Failed to save personal selection to localStorage:', e);
      }
    }
    prevOrgId.current = organization?.id || null;
  }, [organization?.id, hasSelectedPersonal, user?.id]);


  // Fetch internal org ID from organizations endpoint when user is signed in
  React.useEffect(() => {
    const fetchInternalOrgId = async () => {
      if (!isSignedIn || !memoizedGetToken) {
        setIsOrgLoading(false);
        return;
      }
      
      // If user has a Clerk organization, fetch it
      if (organization?.id) {
        setHasSelectedPersonal(false); // Clear personal flag when switching to org
        if (user?.id) {
          try {
            const clerkKey = `hasSelectedPersonal_${user.id}`;
            localStorage.removeItem(clerkKey);
          } catch (e) {
            console.warn('Failed to clear personal selection from localStorage:', e);
          }
        }
        setIsOrgLoading(true);
        try {
          const token = await memoizedGetToken();
          if (!token) {
            setIsOrgLoading(false);
            return;
          }
          
          const queryParam = `clerk_id=${organization.id}`;
          
          
          const response = await fetch(`/api/organizations?${queryParam}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const orgData = await response.json();
            setInternalOrgId(orgData.id);
            setError(null);
          } else {
            const errorText = `Failed to fetch organization (${response.status})`;
            setError(errorText);
            setInternalOrgId(null);
          }
        } catch (error) {
          console.error('Failed to fetch internal org ID:', error);
          setError(error instanceof Error ? error.message : 'Failed to fetch organization');
          setInternalOrgId(null);
        } finally {
          setIsOrgLoading(false);
        }
      } else if (hasSelectedPersonal) {
        // User has explicitly selected personal account, fetch personal org
        setIsOrgLoading(true);
        try {
          const token = await memoizedGetToken();
          if (!token) {
            setIsOrgLoading(false);
            return;
          }
          
          
          const response = await fetch(`/api/organizations?is_personal=true`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          
          if (response.ok) {
            const orgData = await response.json();
            setInternalOrgId(orgData.id);
            setError(null);
          } else {
            const errorText = `Failed to fetch personal organization (${response.status})`;
            setError(errorText);
            setInternalOrgId(null);
          }
        } catch (error) {
          console.error('Failed to fetch personal org ID:', error);
          setError(error instanceof Error ? error.message : 'Failed to fetch personal organization');
          setInternalOrgId(null);
        } finally {
          setIsOrgLoading(false);
        }
      } else {
        // No Clerk organization - auto-select personal account
        setHasSelectedPersonal(true);
        if (user?.id) {
          try {
            const clerkKey = `hasSelectedPersonal_${user.id}`;
            localStorage.setItem(clerkKey, 'true');
          } catch (e) {
            console.warn('Failed to save personal selection to localStorage:', e);
          }
        }
        // This will trigger the personal org fetch on the next effect run
        setInternalOrgId(null);
        setIsOrgLoading(false);
      }
    };

    fetchInternalOrgId();
  }, [isSignedIn, organization?.id, hasSelectedPersonal, memoizedGetToken, user?.id]);
  
  const value: AuthContextType = {
    isAuthenticated: isSignedIn || false,
    isLoading: !authLoaded,
    user,
    organization,
    internalOrgId,
    isOrgLoading,
    error,
    logout: signOut,
    getToken: memoizedGetToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
